import { Box, Typography, Chip, Tooltip, IconButton, Paper } from '@mui/material';
import { Edit, Close, Delete, ContentCopy, FlightTakeoff } from '@mui/icons-material';
import FlexBetween from 'components/FlexBetween';
import { TOTAL_MIN, toMin } from './constants';
import DaysRow from './DaysRow';


const FlightDetailStrip = ({ 
  flight, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onClose, 
  canUpdate, 
  canCreate 
}) => {
  if (!flight) return null;
  const blk         = flight.blockTime || 0;
  const statusColor = { active: 'success', cancelled: 'error', planned: 'default' }[flight.status] || 'default';

  return (
    <Paper 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        border: `1px solid`, 
        borderColor: `primary.main`, 
        borderRadius: 0,
      }}
    >
      {/* Header bar */}
      <Box 
        sx={{ 
          bgcolor: 'blue.main', 
          px: 1.5, 
          py: 0.5, 
          display: 'flex', 
          flexShrink: 0,
          alignItems: 'center', 
          gap: 0.5, 
        }}
      >
        <FlightTakeoff sx={{ color: 'text.primary', fontSize: '1rem' }} />
        <Typography sx={{ color: 'text.primary', fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.8rem' }}>
          {flight.carrier}{flight.flightNo}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {flight.dep}-{flight.arr}
        </Typography>
        {flight.acType && <Chip label={flight.acType} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'text.primary', fontSize: '0.62rem', height: 18 }} />}
        <Chip 
          label={flight.status || 'planned'} 
          size="small" 
          color={statusColor} 
          sx={{ fontSize: '0.62rem', height: 18 }} 
        />

        <Box flex={1} />
                
        {canCreate && (
          <Tooltip title="Duplicate (Ctrl+D)">
            <IconButton size="small" onClick={() => onDuplicate(flight)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <Tooltip title="Edit (E)">
            <IconButton size="small" onClick={() => onEdit(flight)} sx={{ color: 'rgba(255,255,255,0.85)' }}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <Tooltip title="Delete (Del)">
            <IconButton size="small" onClick={() => onDelete(flight)} sx={{ color: 'rgba(255,160,100,0.85)' }}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close (Esc)">
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.85)' }}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Detail body - 12-col grid */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        alignContent: { xs: 'start', md: 'stretch' },
        overflowY: { xs: 'auto', md: 'hidden' },
      }}>
        {/* Col 1-4 (xs: 1-6): Times */}
        <Box
          sx={{
            gridColumn: { xs: 'span 6', md: 'span 4' },
            px: 2,
            py: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            borderRight: `1px solid`,
            borderColor: `divider`,
            overflow: 'hidden',
            gap: 1,
          }}
        >
          <Box display="flex" gap={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">STD</Typography>
              <Typography sx={{ fontFamily: 'monospace', lineHeight: 1 }}>
                {flight.std}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {flight.dep}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <Typography color="text.secondary">→</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">STA</Typography>
              <Typography sx={{ fontFamily: 'monospace', lineHeight: 1 }}>
                {flight.sta}{flight.nextDay > 0 ? <sup style={{ color: '#e65100' }}>+{flight.nextDay}</sup> : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {flight.arr}
              </Typography>
            </Box>
          </Box>
          {blk > 0 && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              fontFamily="monospace"
            >
              Block {Math.floor(blk / 60)}h{String(blk % 60).padStart(2, '0')}m
            </Typography>
          )}
        </Box>

        {/* Col 5-8 (xs: 7-12): Days of operation */}
        <Box sx={{
          gridColumn: { xs: 'span 6', md: 'span 4' },
          px: 2,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
          borderRight: `1px solid`,
          borderColor: 'divider',
          overflow: 'hidden',
        }}>
          <Typography variant="caption" color="text.secondary">
            Days of Operation
          </Typography>
          <DaysRow daysOfOperation={flight.daysOfOperation || ''} size={20} />
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {(flight.daysOfOperation || '').length}x per week
          </Typography>
        </Box>

        {/* Col 9-12: Mini 24h timeline — hidden on xs */}
        <Box sx={{
          gridColumn: 'span 4',
          px: 2,
          py: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1,
          overflow: 'hidden',
        }}>
          <Typography variant="caption" color="text.secondary">
            24h position
          </Typography>
          <Box 
            sx={{ 
              position: 'relative', 
              height: 20, 
              bgcolor: 'action.hover', 
              borderRadius: 1, 
              overflow: 'hidden',
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                left: 0, 
                width: '25%', 
                top: 
                0, 
                bottom: 0, 
                bgcolor: 'rgba(0,0,0,0.07)', 
              }} 
            />
            <Box 
              sx={{ 
                position: 'absolute', 
                left: '91.7%', 
                width: '8.3%', 
                top: 0, 
                bottom: 0, 
                bgcolor: 'rgba(0,0,0,0.07)',
            }} 
            />
            {[6, 12, 18].map((h) => (
              <Box 
                key={h} 
                sx={{ 
                  position: 'absolute', 
                  left: `${(h / 24) * 100}%`, 
                  top: 0, 
                  bottom: 0, 
                  width: 1, 
                  bgcolor: 'divider',
                }} 
              />
            ))}
            <Box 
              sx={{
                position: 'absolute',
                left: `${(toMin(flight.std) / TOTAL_MIN) * 100}%`,
                width: `${Math.max(1.5, ((flight.blockTime || 60) / TOTAL_MIN) * 100)}%`,
                top: 3, bottom: 3, borderRadius: '3px',
                bgcolor: 'primary.main',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} 
            />
          </Box>
          <FlexBetween>
            {['00', '06', '12', '18', '24'].map((h) => (
              <Typography 
                key={h} 
                sx={{ 
                  fontSize: '0.6rem', 
                  color: 'text.disabled', 
                  fontFamily: 'monospace' 
                }}
              >
                {h}
              </Typography>
            ))}
          </FlexBetween>
          {flight.notes && (
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ mt: 0.5, fontStyle: 'italic' }} 
              noWrap
            >
              {flight.notes}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default FlightDetailStrip;
